<template>
    <section class="cashflow-card" :class="{ 'cashflow-card--dialog': dialogMode }">
        <div class="cashflow-card__header">
            <h2 :id="titleId" class="cashflow-card__title">Returns & Cashflow</h2>
            <div class="units" role="group" aria-label="Cashflow units">
                <span>Units</span>
                <div class="units__stepper">
                    <button
                        type="button"
                        :aria-label="`Decrease cashflow units by ${lotSize}`"
                        :disabled="units <= minUnits"
                        @click="decrementUnits"
                    >
                        -
                    </button>
                    <strong role="status" aria-live="polite" :aria-label="`Selected cashflow units: ${units}`">
                        {{ units }}
                    </strong>
                    <button
                        type="button"
                        :aria-label="`Increase cashflow units by ${lotSize}`"
                        :disabled="maxUnits > 0 && units + lotSize > maxUnits"
                        @click="incrementUnits"
                    >
                        +
                    </button>
                </div>
            </div>
        </div>

        <BondV2SignupBlurGate
            :gated="showSignupPayoutGate"
            :message="cashflowGateMessage"
            @signup-required="handleSignupGate"
        >
            <div class="tabs" role="list" aria-label="Cashflow type">
                <span class="tabs__chip tabs__chip--active" role="listitem">{{ data.cashflowTabs.interest }}</span>
                <span class="tabs__chip" role="listitem">{{ data.cashflowTabs.principal }}</span>
            </div>

            <div class="summary-grid" role="list" aria-label="Cashflow summary">
                <div
                    v-for="item in data.cashflowSummary"
                    :key="item.label"
                    :class="['summary-card', { 'summary-card--highlight': item.highlight }]"
                    role="listitem"
                >
                    <strong :class="{ 'summary-card__amount--green': item.highlight }">{{ item.value }}</strong>
                    <span>
                        {{ item.label }}
                        <VTooltip
                            :text="summaryTooltips[item.label] ?? ''"
                            location="top"
                            class="cashflow-summary-tooltip"
                            :aria-label="`${item.label} information tooltip`"
                        >
                            <template #activator="{ props }">
                                <button
                                    v-bind="props"
                                    type="button"
                                    class="summary-info-icon-wrapper"
                                    :aria-label="`More information about ${item.label}`"
                                >
                                    <VIcon class="summary-card__info-icon" size="14" aria-hidden="true">
                                        mdi-information-outline
                                    </VIcon>
                                </button>
                            </template>
                        </VTooltip>
                    </span>
                </div>
            </div>

            <div class="payout-schedule">
                <h3 class="payout-schedule__title">Interest Payout Schedule</h3>
                <div class="table-wrap">
                    <table>
                        <caption class="sr-only">
                            Interest payout schedule for selected units, with payout date, interest amount, and principal amount.
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col">Payout Date</th>
                                <th scope="col">Interest (₹)</th>
                                <th scope="col">Principal (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in visibleCashflows" :key="row.date">
                                <th scope="row">{{ row.date }}</th>
                                <td>{{ row.interest }}</td>
                                <td>{{ row.principal }}</td>
                            </tr>
                            <tr v-if="showTotalRow" class="cashflow-total-row">
                                <th scope="row">Total</th>
                                <td>{{ totalRow.interest }}</td>
                                <td>{{ totalRow.principal }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <button
                v-if="!showSignupPayoutGate && showCashflowToggle"
                class="view-more"
                type="button"
                :aria-expanded="showAllCashflows"
                @click="showAllCashflows = !showAllCashflows"
            >
                {{ showAllCashflows ? 'View Less' : 'View More' }}
                <img
                    :class="{ 'view-more__icon--expanded': showAllCashflows }"
                    :src="viewMoreIcon"
                    alt=""
                    aria-hidden="true"
                />
            </button>
        </BondV2SignupBlurGate>
    </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import viewMoreIcon from '@/assets/icons/bond-details-v2/view-more-double-chevron.svg'
import BondV2SignupBlurGate from './BondV2SignupBlurGate.vue'
import { useBondV2Data } from './bondV2Context'

interface Props {
    units: number
    minUnits: number
    maxUnits: number
    lotSize: number
    titleId?: string
    dialogMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    titleId: 'cashflow-title',
    dialogMode: false
})
const emit = defineEmits<{
    'units-changed': [units: number]
    'signup-required': [payload: SignupGatePayload]
}>()

interface SignupGatePayload {
    title: string
    message: string
    ctaName: string
}

const data = useBondV2Data()
const maxDefaultRows = 8
const showAllCashflows = ref(false)

const cashflowSummaryValue = (label: string) =>
    data.value.cashflowSummary.find((item) => item.label === label)?.value ?? '₹0.00'

const visibleCashflows = computed(() => {
    if (props.dialogMode || showAllCashflows.value || data.value.cashflows.length <= maxDefaultRows) {
        return data.value.cashflows
    }

    return data.value.cashflows.slice(0, maxDefaultRows)
})

const showCashflowToggle = computed(() => !props.dialogMode && data.value.cashflows.length > maxDefaultRows)
const showSignupPayoutGate = computed(() => !props.dialogMode)
const cashflowGateMessage = computed(() => 'Sign up to view detailed payout schedule')
const showTotalRow = computed(() => visibleCashflows.value.length === data.value.cashflows.length)

const totalRow = computed(() => ({
    interest: data.value.cashflowTotals.interest || cashflowSummaryValue('Interest earned'),
    principal: data.value.cashflowTotals.principal || 'N/A'
}))

watch(() => data.value.cashflows, () => {
    showAllCashflows.value = false
})

const summaryTooltips: Record<string, string> = {
    'Principal Invested': 'Principal Invested is the amount used to purchase the selected bond units, excluding future interest.',
    'Interest earned': 'Interest earned is the total coupon interest expected for the selected units over the cashflow schedule.',
    'Final Payout': 'Final Payout is the total expected amount received, including principal repayment and interest.'
}

const clampUnits = (value: number) => {
    let units = Math.floor(Number(value))
    if (Number.isNaN(units)) units = props.minUnits
    units = Math.floor(units / props.lotSize) * props.lotSize
    if (units < props.minUnits) units = props.minUnits
    if (props.maxUnits > 0 && units > props.maxUnits) units = props.maxUnits
    return units
}

const incrementUnits = () => {
    emit('units-changed', clampUnits(props.units + props.lotSize))
}

const decrementUnits = () => {
    emit('units-changed', clampUnits(props.units - props.lotSize))
}

const handleSignupGate = () => {
    if (!showSignupPayoutGate.value) return
    emit('signup-required', {
        title: 'Sign Up to View Cashflow',
        message: 'Sign up to access detailed cashflow schedules and see your potential returns.',
        ctaName: 'signup-cashflow-bond-details'
    })
}
</script>

<style scoped>
.cashflow-card {
    border: none;
    border-radius: 7px;
    background: #F9FBFF;
    overflow: hidden;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
    box-sizing: border-box;
}

.cashflow-card--dialog {
    border-radius: 7px;
    max-height: calc(88vh - 112px);
    overflow: auto;
}

.cashflow-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 0 0 16px;
}

.cashflow-card__title {
    margin: 0;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 14px;
    font-weight: 800;
}

.units {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 12px;
    font-weight: 700;
}

.units__stepper {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    padding: 4px;
    border-radius: 3px;
}

.units button {
    width: 18px;
    height: 18px;
    border: none;
    border-radius: 4px;
    color: var(--bond-v2-primary, var(--color-primary-40));
    background: var(--bond-v2-soft, var(--color-primary-90));
    cursor: pointer;
    line-height: 18px;
    padding: 0;
}

.units button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.units strong {
    color: var(--bond-v2-text, var(--color-text-primary));
    font-size: 12px;
    font-weight: 800;
}

.tabs {
    display: flex;
    gap: 8px;
    padding: 0 0 12px;
}

.tabs__chip {
    border: none;
    border-radius: 12px;
    background: #fae6ff;
    color: #9a008f;
    display: flex;
    height: 20px;
    padding: 8px;
    justify-content: center;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    font-weight: 800;
}

.tabs__chip--active {
    color: #00619a;
    background: #e6f3ff;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    background: transparent;
}

.summary-card {
    padding: 10px 18px;
    background: #fff;
    border-radius: 4px;
}

.summary-card--highlight {
    background: #f1ffec;
}

.summary-card strong {
    display: block;
    color: #1b2559;
    font-family: Inter, var(--font-family-primary);
    font-size: 13px;
    font-style: normal;
    font-weight: 900;
    line-height: 13px;
    text-align: left;
}

.summary-card strong.summary-card__amount--green {
    color: #067a49;
}

.summary-card span {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
    color: #5b6684;
    text-align: left;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 0;
}

.summary-info-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;
    padding: 0;
}

.summary-card__info-icon {
    color: #0027af;
    font-size: 14px;
}

.payout-schedule {
    margin-top: 12px;
    border: 1px solid var(--bond-v2-border, #eaf7ff);
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}

.payout-schedule__title {
    margin: 0;
    padding: 18px 20px 0;
    color: var(--bond-v2-text, #1b2559);
    font-family: Inter, var(--font-family-primary);
    font-size: 14px;
    font-weight: 800;
    line-height: 18px;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.table-wrap {
    margin-top: 16px;
    overflow-x: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th,
td {
    padding: 14px 20px;
    white-space: nowrap;
}

th {
    color: #5b6684;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-weight: 500;
    line-height: 13px;
    letter-spacing: 0;
    text-align: left;
    background: #f9fbff;
}

td,
tbody th {
    color: #1b2559;
    text-align: left;
    font-family: Inter, var(--font-family-primary);
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 13px;
    letter-spacing: 0;
    background: transparent;
}

tbody tr {
    background: #fff;
}

tbody tr:nth-child(even) {
    background: #f9fbff;
}

.cashflow-total-row {
    background: #fff;
}

.cashflow-total-row td,
.cashflow-total-row th {
    font-weight: 600;
}

.cashflow-total-row th:first-child {
    font-weight: 700;
}

.view-more {
    width: 100%;
    min-height: 48px;
    border: none;
    background: #fff;
    color: var(--bond-v2-primary, var(--color-primary-40));
    font-size: 12px;
    font-weight: 400;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.view-more img {
    width: 9px;
    height: 11px;
    display: block;
    transition: transform 0.2s ease;
}

.view-more__icon--expanded {
    transform: rotate(180deg);
}

@media (max-width: 640px) {
    .cashflow-card {
        border-radius: 0;
        padding: 12px 16px;
        box-sizing: border-box;
    }

    .cashflow-card__header {
        align-items: center;
        flex-direction: row;
        gap: 10px;
    }

    .cashflow-card__title {
        font-size: 13px;
    }

    .tabs {
        overflow-x: auto;
        padding-bottom: 10px;
        scrollbar-width: none;
    }

    .tabs::-webkit-scrollbar {
        display: none;
    }

    .tabs__chip {
        flex: 0 0 auto;
    }

    .summary-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 6px;
    }

    .summary-card {
        min-width: 0;
        padding: 8px 6px;
    }

    .summary-card strong {
        font-size: 11px;
        line-height: 13px;
    }

    .summary-card span {
        align-items: flex-start;
        font-size: 10px;
        line-height: 13px;
        letter-spacing: 0;
        min-width: 0;
        overflow-wrap: anywhere;
    }

    .payout-schedule__title {
        padding: 16px 14px 0;
        font-size: 13px;
    }

    table {
        min-width: 0;
        table-layout: fixed;
    }

    th,
    td {
        padding: 9px 5px;
        white-space: normal;
        overflow-wrap: anywhere;
    }
}
</style>

<style>
.cashflow-summary-tooltip .v-overlay__content,
.cashflow-summary-tooltip.v-tooltip .v-overlay__content {
    min-width: 180px !important;
    max-width: 280px !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    text-align: left !important;
    font-size: 12px !important;
}
</style>
